import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { BillingBasis, BillingStatus, TripBillingRow, TripStatus } from '../lib/tripLifecycle';

export interface TripBillingOverview extends TripBillingRow {
  trip_id: string;
  project_number: string;
  customer_id: string;
  customer_name: string | null;
  location_id: string;
  location_name: string | null;
  deployment_id: string | null;
  lead_technician_id: string | null;
  lead_technician_name: string | null;
  scope: string | null;
  start_date: string | null;
  end_date: string | null;
  po_number: string | null;
  solution_types: string[] | null;
  completed_at: string | null;
  cancelled_at: string | null;
  invoice_reference: string | null;
  invoiced_at: string | null;
  billing_notes: string | null;
  report_count: number;
  submitted_report_count: number;
  last_report_date: string | null;
  estimated_amount_cents: number | string | null;
}

const OVERVIEW_COLUMNS =
  'trip_id, project_number, customer_id, customer_name, location_id, location_name, deployment_id, ' +
  'lead_technician_id, lead_technician_name, scope, trip_status, start_date, end_date, po_number, ' +
  'solution_types, completed_at, cancelled_at, billing_basis, hourly_rate_cents, flat_fee_cents, ' +
  'billing_status, invoice_reference, invoiced_at, billing_notes, report_count, submitted_report_count, ' +
  'last_report_date, labor_hours, reports_missing_hours, estimated_amount_cents, is_overdue';

// trip_billing_overview and the billing RPCs are newer than database.types.ts;
// regenerate types after `supabase db reset` and drop these casts.
type Untyped = {
  from: (t: string) => {
    select: (cols: string) => {
      order: (c: string, o: { ascending: boolean; nullsFirst?: boolean }) => Promise<{ data: unknown[] | null; error: { message: string } | null }>;
      eq: (c: string, v: string) => { maybeSingle: () => Promise<{ data: unknown | null; error: { message: string } | null }> };
    };
  };
  rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
};
const db = supabase as unknown as Untyped;

export const TRIP_BILLING_KEY = ['admin-trip-billing'] as const;

export function useTripBillingOverview() {
  return useQuery({
    queryKey: TRIP_BILLING_KEY,
    queryFn: async () => {
      const { data, error } = await db
        .from('trip_billing_overview')
        .select(OVERVIEW_COLUMNS)
        .order('start_date', { ascending: false, nullsFirst: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as TripBillingOverview[];
    },
    staleTime: 60_000,
  });
}

export function useTripBilling(tripId: string | null) {
  return useQuery({
    queryKey: [...TRIP_BILLING_KEY, tripId],
    enabled: Boolean(tripId),
    queryFn: async () => {
      const { data, error } = await db
        .from('trip_billing_overview')
        .select(OVERVIEW_COLUMNS)
        .eq('trip_id', tripId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data ?? null) as TripBillingOverview | null;
    },
    staleTime: 30_000,
  });
}

/** Everything a trip or billing change can move. */
function useInvalidateTrips() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: TRIP_BILLING_KEY }),
      qc.invalidateQueries({ queryKey: ['admin-field-trips'] }),
      qc.invalidateQueries({ queryKey: ['admin-customer-detail'] }),
    ]);
}

/** Postgres raises readable messages from the guard; pass them through. */
function rethrow(error: { message: string } | null) {
  if (error) throw new Error(error.message);
}

export function useTransitionTrip() {
  const invalidate = useInvalidateTrips();
  return useMutation({
    mutationFn: async ({ tripId, to }: { tripId: string; to: TripStatus }) => {
      const { error } = await supabase.from('field_trips').update({ status: to }).eq('id', tripId);
      rethrow(error);
    },
    onSuccess: invalidate,
  });
}

export interface TripEdit {
  start_date: string;
  end_date: string;
  lead_technician_id: string | null;
  scope: string | null;
  po_number: string | null;
}

export function useUpdateTrip() {
  const invalidate = useInvalidateTrips();
  return useMutation({
    mutationFn: async ({ tripId, patch }: { tripId: string; patch: TripEdit }) => {
      const { error } = await supabase.from('field_trips').update(patch).eq('id', tripId);
      rethrow(error);
    },
    onSuccess: invalidate,
  });
}

export function useSetBillingTerms() {
  const invalidate = useInvalidateTrips();
  return useMutation({
    mutationFn: async (args: {
      tripId: string;
      basis: BillingBasis;
      hourlyRateCents: number | null;
      flatFeeCents: number | null;
      notes: string | null;
    }) => {
      const { error } = await db.rpc('set_trip_billing_terms', {
        p_trip_id: args.tripId,
        p_basis: args.basis,
        p_hourly_rate_cents: args.hourlyRateCents,
        p_flat_fee_cents: args.flatFeeCents,
        p_notes: args.notes,
      });
      rethrow(error);
    },
    onSuccess: invalidate,
  });
}

export function useSetBillingOutcome() {
  const invalidate = useInvalidateTrips();
  return useMutation({
    mutationFn: async (args: { tripId: string; status: BillingStatus; invoiceReference?: string | null; notes?: string | null }) => {
      const { error } = await db.rpc('set_trip_billing_outcome', {
        p_trip_id: args.tripId,
        p_status: args.status,
        p_invoice_reference: args.invoiceReference ?? null,
        p_notes: args.notes ?? null,
      });
      rethrow(error);
    },
    onSuccess: invalidate,
  });
}
